import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Star, MessageCircle } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import PremiumServicesSection from '../components/PremiumServicesSection';
import BlogSection from '../components/BlogSection';
import SimpleBookingSection from '../components/SimpleBookingSection';
import GoogleReviewsSection from '../components/GoogleReviewsSection';
import GallerySection from '../components/GallerySection';
import ChatWidget from '../components/ChatWidget';
import { Button } from '../components/ui/button';

const Home = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showChatButton, setShowChatButton] = useState(false);

  // Show chat button after scrolling 300px
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowChatButton(true);
      } else {
        setShowChatButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <a href="tel:9056996599" className="flex items-center space-x-3 hover:opacity-80 transition-opacity group">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">cellrox</h1>
                <p className="text-xs text-cyan-600 font-semibold">📞 905-699-6599</p>
              </div>
            </a>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-slate-700 hover:text-cyan-600 font-medium transition-colors">Services</a>
              <a href="#gallery" className="text-slate-700 hover:text-cyan-600 font-medium transition-colors">Gallery</a>
              <a href="https://share.google/NODHmGNeIm3tve6Wc" target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-cyan-600 font-medium transition-colors">Reviews</a>
              <a href="#booking" className="btn-primary">Book Now</a>
            </nav>

            <a href="#booking" className="md:hidden btn-primary">Book Now</a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <HeroSection />
        
        {/* Trust Badges */}
        <section className="py-12 bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">90-Day Warranty</h3>
                <p className="text-sm text-slate-600">All repairs covered</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
                  <Clock className="w-8 h-8 text-cyan-500" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">Same-Day Service</h3>
                <p className="text-sm text-slate-600">Most repairs in 30-60 min</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
                  <Star className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">5-Star Rated</h3>
                <p className="text-sm text-slate-600">Trusted by hundreds</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
                  <MapPin className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">Inside Walmart</h3>
                <p className="text-sm text-slate-600">Convenient location</p>
              </div>
            </div>
          </div>
        </section>

        <PremiumServicesSection />
        <GallerySection />
        <GoogleReviewsSection />
        <BlogSection />
        <SimpleBookingSection />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">cellrox</h3>
                  <p className="text-xs text-slate-400">Expert iPhone Repair</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your trusted iPhone repair experts inside Walmart. Professional service, competitive prices, 90-day warranty.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#services" className="text-slate-400 hover:text-cyan-400 transition-colors">Services</a></li>
                <li><a href="#gallery" className="text-slate-400 hover:text-cyan-400 transition-colors">Gallery</a></li>
                <li><a href="#booking" className="text-slate-400 hover:text-cyan-400 transition-colors">Book Appointment</a></li>
                <li><a href="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors">Blog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <a href="tel:9056996599" className="text-slate-400 hover:text-cyan-400 transition-colors block">905-699-6599</a>
                    <a href="tel:6479976390" className="text-slate-400 hover:text-cyan-400 transition-colors block">647-997-6390</a>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <a href="mailto:info@iphonerepairnearme.ca" className="text-slate-400 hover:text-cyan-400 transition-colors">
                    info@iphonerepairnearme.ca
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Location</h4>
              <div className="flex items-start space-x-3 mb-4">
                <MapPin className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400">
                  800 Matheson Blvd West<br />
                  Inside Walmart<br />
                  Mississauga, ON
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400">
                  Mon-Sun: 10:00 AM - 10:00 PM
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-slate-400 text-sm">
                © 2025 cellrox. Professional Device Repair.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">Privacy Policy</a>
                <a href="#" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Chat Widget Button - appears on scroll */}
      {showChatButton && !isChatOpen && (
        <Button
          data-testid="chat-toggle-button"
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-2xl bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 z-50 transition-all duration-300 hover:scale-110 animate-fade-in"
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </Button>
      )}

      {/* Chat Widget */}
      <ChatWidget isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

export default Home;
